export type SignupRole = "patient" | "doctor"
export type SignupOutcome = { kind: "existing" } | { kind: "new" } | { kind: "error"; message: string }

export interface D2cMeResponse {
  ok?: boolean
  enabledRoles?: string[]
  hasClinic?: boolean
  clinicUserId?: string | null
  [key: string]: unknown
}

export interface D2cSignupApi {
  me: () => Promise<D2cMeResponse | unknown>
  provision: (role: SignupRole) => Promise<{ ok?: boolean; error?: string } | undefined | void>
}

const DEFAULT_ERROR = "Failed to set up your account. Please try again."

/**
 * First-sign-in handling for the D2C surface.
 * - 401 or 404 from me() indicates a brand-new user who has not been provisioned in app_users yet.
 * - Network errors, timeouts, or 5xx from me() must fail closed with kind: "error" so the user
 *   sees a retryable setup error and is not navigated away in a broken state.
 * - For an existing patient who chooses doctor (or a doctor whose clinic provisioning failed previously),
 *   doctor provisioning runs based on missing clinic state (hasClinic !== true).
 * - For a returning doctor who already has a clinic (hasClinic === true), provisioning is skipped.
 * - For an existing patient signing in as patient, provisioning is skipped.
 */
export async function runD2cSignup(role: SignupRole, api: D2cSignupApi): Promise<SignupOutcome> {
  let exists = false
  let meResult: D2cMeResponse | null = null

  try {
    const res = await api.me()
    meResult = (res && typeof res === "object") ? (res as D2cMeResponse) : null
    exists = true
  } catch (err: any) {
    if (err?.status === 404 || err?.status === 401) {
      exists = false
    } else {
      return { kind: "error", message: err?.message ?? DEFAULT_ERROR }
    }
  }

  if (exists) {
    if (role === "patient") {
      return { kind: "existing" }
    }
    if (role === "doctor" && meResult?.hasClinic) {
      return { kind: "existing" }
    }
  }

  try {
    const res = await api.provision(role)
    if (res && res.ok === false) {
      return { kind: "error", message: res.error ?? DEFAULT_ERROR }
    }
  } catch (e: any) {
    return { kind: "error", message: e?.message ?? DEFAULT_ERROR }
  }

  return { kind: exists ? "existing" : "new" }
}
